import {Module} from '@fantasmagames/slot/src/common/Module';
import {RecodeEvents} from '@fantasmagames/slot/src/integration/RecodeEvents';
import {RecodeIntegrator} from '@fantasmagames/slot/src/integration/RecodeIntegrator';
import {RecodeLogic} from '@fantasmagames/slot/src/logic/integration/RecodeLogic';
import {LogicEvents} from '@fantasmagames/slot/src/logic/LogicEvents';
import {BackgroundModule} from '@fantasmagames/slot/src/modules/background/BackgroundModule';
import {FlowModuleEvents} from '@fantasmagames/slot/src/modules/flow/FlowModuleEvents';
import {InfoEvents} from '@fantasmagames/slot/src/modules/hud/info/InfoEvents';
import {SpinButtonEvents} from '@fantasmagames/slot/src/modules/keypad/spinbutton/SpinButtonEvents';
import {StopAutoplayButtonEvents} from '@fantasmagames/slot/src/modules/keypad/stopautoplaybutton/StopAutoplayButtonEvents';
import {SettingsEvents} from '@fantasmagames/slot/src/modules/mainmenu/settingsmenu/SettingsEvents';
import {FreeRoundsScreenEvents} from '@fantasmagames/slot/src/modules/microgaming/freerounds/FreeRoundsScreenEvents';
import {AutoplayMenuEvents} from '@fantasmagames/slot/src/modules/popupmenus/autoplaymenu/AutoplayMenuEvents';
import {BetlevelMenuEvents} from '@fantasmagames/slot/src/modules/popupmenus/betmenu/BetlevelMenuEvents';
import {FreeSpinsEvents} from '@fantasmagames/slot/src/modules/freespins/intro/FreeSpinsEvents';

export class RecodeModule extends Module<null> {
	protected get api(): RecodeIntegrator {
		return (this.logic as RecodeLogic).communicator as RecodeIntegrator;
	}
	protected _systemNewBalance: number;
	protected _systemBalanceUpdateDirty: boolean;
	protected _canSpin: boolean;
	protected _hasBetMessageWindowOnScreen: boolean = false;

	private _initialized: boolean = false;
	private _backgroundInitialized: boolean = false;
	private _loadingEnded: boolean = false;

	public init(): void {
		super.init();

		this.api.setModule(this);
	}

	public restore(): void {
		this.onClientGameplayStart();
	}

	public emitValidatedBetCoinSizeChanged(): void {
		this.eventManager.emit(RecodeEvents.VALIDATED_BET_COIN_SIZE_CHANGED);
	}

	public onShowErrorDialog(): void {
		this.eventManager.emit(RecodeEvents.SHOWING_ERROR);
	}

	public onCloseErrorDialog(): void {
		this.eventManager.emit(RecodeEvents.CLOSED_ERROR_DIALOG);
	}

	public onServerSystemBalanceUpdate(newBalance: number): void {
		// New Balance in cents.
		// Check if the player can spin ATM.
		this._systemBalanceUpdateDirty = true;
		this._systemNewBalance = newBalance;

		if (this._canSpin === true) {
			this._systemBalanceUpdateDirty = false;
			(this.logic as RecodeLogic).systemUpdateBalance(this._systemNewBalance);
			this.eventManager.emit(RecodeEvents.SERVER_SYSTEM_BALANCE_UPDATE, newBalance);
			this._systemNewBalance = 0;
		}
	}

	public onServerSetMute(value: boolean): void {
		// options {optionType: 'MUTE', value:boolean}
		this.eventManager.emit(RecodeEvents.SERVER_PLUGIN_SET_MUTE, value);
	}

	public onServerGameplayResume(): void {
		// No data needed
		this.eventManager.emit(RecodeEvents.SERVER_GAMEPLAY_RESUME);
	}

	public onServerAutoplayInterrupt(...args: any[]): void {
		// No data needed
		this.eventManager.emit(RecodeEvents.SERVER_AUTOPLAY_INTERRUPT, args);
	}

	public onServerPreloaderRemoved(): void {
		// No data needed
		this.eventManager.emit(RecodeEvents.SERVER_PRELOADER_REMOVED);
	}

	public onServerLaunchLobby(): void {
		this.eventManager.emit(RecodeEvents.SERVER_LAUNCH_LOBBY);
	}

	public onServerLaunchBanking(): void {
		this.eventManager.emit(RecodeEvents.SERVER_LAUNCH_BANKING);
	}

	protected setupStates(): void {}

	protected enterFirstState(): void {}

	protected setupViews(): void {}

	protected setupEvents(): void {
		this.on(BackgroundModule.BACKGROUND_CREATED_EVENT, this.onBackgroundCreated);

		this.on(AutoplayMenuEvents.ACCEPTED, this.onStartAutoPlay);
		this.on(AutoplayMenuEvents.CANCELLED, this.onAutoplayMenuCancelled);
		this.on(AutoplayMenuEvents.AUTO_PLAY_MENU_OPENED, this.onAutoplayMenuOpened);

		this.on(StopAutoplayButtonEvents.STOP_AUTOPLAY_BUTTON_CLICKED, this.onStopAutoPlay);

		this.on(FlowModuleEvents.WON, this.onWon);
		this.on(FlowModuleEvents.CAN_SPIN, this.onCanSpin);

		this.on(FreeSpinsEvents.FREE_SPINS_OUTRO_FINISHED, this.onFreeSpinsOutroFinished);

		this.on(SpinButtonEvents.SPIN_BUTTON_PRESSED, this.onSpinButtonPressed);

		this.on(SettingsEvents.TOGGLE_ALL_SOUND_OFF, this.onToggleSoundOff);
		this.on(SettingsEvents.TOGGLE_ALL_SOUND_ON, this.onToggleSoundOn);

		// Added for user input on sound icon in info bar.
		this.on(InfoEvents.TOGGLE_ALL_SOUND_OFF, this.onToggleSoundOff);
		this.on(InfoEvents.TOGGLE_ALL_SOUND_ON, this.onToggleSoundOn);

		this.on(LogicEvents.INIT_RESPONSE, this.onInitResponse);

		this.on(BetlevelMenuEvents.BET_VALUE_UPDATED, this.onBetValueUpdated);
		this.on(BetlevelMenuEvents.BET_LEVEL_MENU_OPENED, this.onBetLevelMenuOpened);
		this.on(BetlevelMenuEvents.BET_LEVEL_MENU_CANCELLED, this.onBetLevelMenuCancelled);
		this.on(BetlevelMenuEvents.BET_LEVEL_MENU_SELECTED_BET_ACCEPTED, this.onBetLevelMenuAccepted);

		this.on(InfoEvents.BALANCE_UPDATED, this.onBalanceUpdated);

		this.on(FreeRoundsScreenEvents.FREE_GAMES_FINISHED, this.onFreeGamesFinished);
		this.on(FreeRoundsScreenEvents.FREE_GAMES_POSTPONED, this.onFreeGamesPostponed);
		this.on(FreeRoundsScreenEvents.FREE_GAMES_REJECTED, this.onFreeGamesRejected);
		this.on(FreeRoundsScreenEvents.FREE_GAME_EXPIRED, this.onFreeGamesExpired);
	}

	protected onBackgroundCreated(): void {
		// this._backgroundInitialized = true;
		// this.endLoading();
	}

	protected onFreeGamesFinished(): void {
		if (!(this.logic as RecodeLogic).hasKilledFreeGamesRestore) {
			this.validatePreviousBet();
		}
	}

	protected onFreeGamesPostponed(): void {
		this.validatePreviousBet();
	}

	protected onFreeGamesRejected(): void {
		this.validatePreviousBet();
	}

	protected onFreeGamesExpired(): void {
		this.validatePreviousBet();
	}

	protected validatePreviousBet(): void {
		if (this._hasBetMessageWindowOnScreen) {
			return;
		}
		// Restore to last bet if it was as valid one after Free Games are finished or differing from bet.
		this._hasBetMessageWindowOnScreen = (this.logic as RecodeLogic).validatePreviousBet(this.emitValidatedBet.bind(this));

		if (this._hasBetMessageWindowOnScreen) {
			this.eventManager.emit(RecodeEvents.VALIDATED_BET_DIALOG_SHOWN);
		}
	}

	protected emitValidatedBet(): void {
		this._hasBetMessageWindowOnScreen = false;

		this.eventManager.emit(RecodeEvents.VALIDATED_BET);
	}

	protected onBalanceUpdated(): void {
		this.onClientBalanceUpdated();
	}

	protected onBetValueUpdated(): void {
		this.api.api_betSizeUpdate(this.logic.betValue / RecodeLogic.CENT_TO_A_DOLLAR);
	}

	protected onBetLevelMenuOpened(): void {
		this.onClientGameplayStart();
	}

	protected onBetLevelMenuCancelled(): void {
		this.onClientGameplayPause();
	}

	protected onBetLevelMenuAccepted(): void {
		this.onClientGameplayPause();
	}

	protected onToggleSoundOff(): void {
		this.api.api_gameSetMute(true);
	}

	protected onToggleSoundOn(): void {
		this.api.api_gameSetMute(false);
	}

	protected onSpinButtonPressed(): void {
		this._canSpin = false;
		this.onClientGameplayStart();
	}

	protected hasRestoreAndNoFreeGames(): boolean {
		return this.logic.hasRestore && !(this.logic as RecodeLogic).hasFreeGames();
	}

	protected onFreeSpinsOutroFinished(): void {
		this.validatePreviousBet();
	}

	protected onCanSpin(): void {
		this._canSpin = true;

		if (!this._initialized) {
			return;
		}

		const logic: RecodeLogic = this.logic as RecodeLogic;

		if (
			this.hasRestoreAndNoFreeGames() ||
			logic.hasClearedExpiredFreeGames ||
			logic.hasKilledFreeGamesRestore ||
			logic.restoredIntoBonusGame
		) {
			logic.hasClearedExpiredFreeGames = false;
			logic.hasKilledFreeGamesRestore = false;
			logic.restoredIntoBonusGame = false;

			if (!logic.isFreeSpinsJustWon() && !logic.isFreeSpinsStarted) {
				this.validatePreviousBet();
			}
		}

		this.onClientGameplayPause();
		this.api.api_winUpdate(this.logic.getWin() / RecodeLogic.CENT_TO_A_DOLLAR); // Assuming its stored in dollars. Convert it to cents.

		// If this is pokerstars integration.
		// TODO: Setup logic for checking after Pokerstars.
		this.api.api_gameRoundCompleted();

		if (this._systemBalanceUpdateDirty === true) {
			this._systemBalanceUpdateDirty = false;
			(this.logic as RecodeLogic).systemUpdateBalance(this._systemNewBalance);
			this.eventManager.emit(RecodeEvents.SERVER_SYSTEM_BALANCE_UPDATE);
			this._systemNewBalance = 0;
		}
	}

	protected endLoading(): void {
		if (this._initialized && !this._loadingEnded) {
			this._loadingEnded = true;
			this.system.endLoading();
			this.onServerPreloaderRemoved();
		}
	}

	protected onInitResponse(): void {
		this.onClientPlayerInformationReceived();
		this.endLoading();
	}

	protected onWon(): void {
		this.onClientWinUpdate();
	}

	protected onAutoplayMenuCancelled(): void {
		this.onClientGameplayPause();
	}

	protected onAutoplayMenuOpened(): void {
		this.onClientGameplayStart();
	}

	protected onStartAutoPlay(): void {
		this.onClientAutoplayStarted();
	}

	protected onStopAutoPlay(): void {
		this.api.api_autoPlayStopped();
	}

	protected onClientGameplayStart(): void {
		this.api.api_gameplayStart();
	}

	protected onClientGameplayPause(): void {
		this.api.api_gameplayPause();
	}

	protected onClientPlayerInformationReceived(): void {
		// Assuming it's provided in cents.
		const playersBalance: number = (this.logic as RecodeLogic).currentBalanceCents;

		this.api.api_playerInformationRecieved(playersBalance);
		if (!this.logic.hasRestore) {
			this.onClientGameplayPause();
		}
		this.api.api_gameSetMute(!this.logic.soundAllActive); // The inverted value of active sound is what mute-state the sound is in.
		this._initialized = true;

		if (this._canSpin) {
			if (
				!(
					this.logic.hasRestore ||
					(this.logic as RecodeLogic).hasFreeGames() ||
					(this.logic as RecodeLogic).hasClearedExpiredFreeGames ||
					(this.logic as RecodeLogic).hasKilledFreeGamesRestore ||
					(this.logic as RecodeLogic).restoredIntoBonusGame
				)
			) {
				this.validatePreviousBet();
			}
		}
	}

	protected onClientSetMute(eventId: string, newValue: boolean): void {
		this.api.api_gameSetMute(newValue);
	}

	protected onClientWinUpdate(): void {
		// Should be converted to "Cents"
		const winValue: number = this.logic.getSpinWin() / RecodeLogic.CENT_TO_A_DOLLAR;
		this.api.api_winUpdate(winValue);
	}

	protected onClientGameMenuOpen(): void {
		this.api.api_gamemenuOpen();
	}

	protected onClientGameMenuClosed(): void {
		this.api.api_gamemenuClosed();
	}

	protected onClientAutoplayStarted(): void {
		this.api.api_autoPlayStarted();
	}

	protected onClientGameRoundCompleted(): void {
		this.api.api_gameRoundCompleted();
	}

	protected onClientBalanceUpdated(): void {
		// const balance: number = this.logic.getSpinBalance() / RecodeLogic.CENT_TO_A_DOLLAR;

		// Either this solution or add balance parsing to RecodeSpinResults,  Response.Player.attributes.balance.
		const balance: number = (this.logic as RecodeLogic).currentBalanceCents;
		this.api.api_balanceUpdate(balance);
	}
}



// WEBPACK FOOTER //
// ./@fantasmagames/slot/src/integration/RecodeModule.ts